import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { completeSpotifyCallback } from '../../services/spotifyAuth';
import { useSpotify } from '../../contexts/SpotifyContext';
import { ROUTES } from '../../constants/routes';
import SpotifyIcon from './SpotifyIcon';

const SpotifyCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { applyConnectionStatus, refreshStatus } = useSpotify();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Connecting Spotify…');
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    const finish = (ok, msg) => {
      setStatus(ok ? 'success' : 'error');
      setMessage(msg);
      if (ok) toast.success(msg);
      else toast.error(msg);
      setTimeout(() => navigate(ROUTES.SPOTIFY, { replace: true }), 1200);
    };

    if (error) {
      finish(false, errorDescription || error || 'Spotify authorization failed');
      return;
    }
    if (!code || !state) {
      finish(false, 'Missing authorization code from Spotify');
      return;
    }

    completeSpotifyCallback({ code, state })
      .then(async (data) => {
        // Apply callback payload immediately so /spotify never flashes "Connect".
        applyConnectionStatus({
          connected: true,
          is_premium: Boolean(data?.is_premium),
          product: data?.product || null,
          display_name: data?.display_name || null,
        });
        try {
          await refreshStatus();
        } catch (_) {
          /* already applied callback status */
        }
        const premiumNote = data?.is_premium
          ? 'You can play music in Tick Sync.'
          : 'Connected. Premium is required for in-app playback.';
        finish(true, `Spotify connected. ${premiumNote}`);
      })
      .catch((err) => {
        const detail =
          err?.response?.data?.detail || err?.message || 'Failed to connect Spotify';
        finish(false, detail);
      });
  }, [applyConnectionStatus, location.search, navigate, refreshStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-secondary-900 p-6">
      <div className="card max-w-md w-full p-8 text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-[#1DB954] flex items-center justify-center">
          {status === 'processing' && <Loader className="h-6 w-6 text-white animate-spin" />}
          {status === 'success' && <CheckCircle className="h-6 w-6 text-white" />}
          {status === 'error' && <XCircle className="h-6 w-6 text-white" />}
          {!['processing', 'success', 'error'].includes(status) && (
            <SpotifyIcon className="h-7 w-7 text-white" />
          )}
        </div>
        <h1 className="text-lg font-semibold text-secondary-900 dark:text-white">Spotify</h1>
        <p className="text-sm text-secondary-600 dark:text-secondary-400">{message}</p>
      </div>
    </div>
  );
};

export default SpotifyCallback;
